<template>
  <div class="bitts-simple-radio-filter">
    <BittsInput
      v-if="searchable"
      v-model="searchQuery"
      :placeholder="placeholder"
      name="radio_filter_search"
      type="search"
      class="mb-8"
      @update:model-value="() => emit('search-applied', searchQuery)"
    />
    <div v-if="!loading">
      <BittsRadioGroupCards
        :options="filteredOptions"
        :initial-value="selected"
        @change="onSelectionChanged"
        class="max-h-[268px] min-w-[244px] overflow-y-auto"
      >
        <template #tooltip="{ option }">
          {{ (option as Option)?.tooltipItems?.text }}
          <BittsButton
            v-if="(option as Option)?.tooltipItems?.buttonText"
            class="my-8 w-full"
            :text="(option as Option)?.tooltipItems?.buttonText"
            size="x-small"
            type="white"
            @click="onTooltipClicked"
          />
        </template>
      </BittsRadioGroupCards>
      <div v-if="!filteredOptions.length" class="py-8 text-center">
        <BittsEmptyState
          :font-awesome-icon="['fak', 'search']"
          :font-awesome-icon-style="{ height: '26px', width: '26px' }"
          font-awesome-icon-class="bitts-simple-radio-filter__empty-state-icon"
          title="No results"
          description="Try a different search term to find fields"
          class="w-[244px] border-none"
        />
      </div>
    </div>
    <div v-else class="py-8 flex justify-center w-[244px]">
      <FontAwesomeIcon
        :icon="['fak', 'loading']"
        spin
        class="text-neutral-accent w-28 h-28"
      />
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import BittsButton from '../../BittsButton/BittsButton.vue';
import BittsEmptyState from '../../BittsEmptyState/BittsEmptyState.vue';
import BittsInput from '../../BittsInput/BittsInput.vue';
import BittsRadioGroupCards from '../../BittsRadioGroupCards/BittsRadioGroupCards.vue';

type Option = {
  label: string;
  value: string | number;
  disabled?: boolean;
  tooltip?: boolean;
  tooltipItems?: { text?: string; buttonText?: string };
};

const {
  value = [],
  options = [],
  loading = false,
  searchable = true,
  placeholder = 'Search for a value',
  search = '',
} = defineProps<{
  value?: string[];
  options?: Option[];
  loading?: boolean;
  searchable?: boolean;
  placeholder?: string;
  search?: string;
}>();

const emit = defineEmits<{
  (e: 'filter-changed', value: string[] | undefined): void;
  (e: 'search-applied', query: string): void;
  (e: 'tooltip-clicked'): void;
}>();

const searchQuery = ref('');

const filteredOptions = computed(() => {
  if (!searchQuery.value) return options;
  return options.filter((option) =>
    option.label.toLowerCase().includes(searchQuery.value.toLowerCase()),
  );
});

const firstOptionValue = computed<string | undefined>(() => {
  if (!filteredOptions.value?.length) return undefined;
  return String(filteredOptions.value[0]?.value);
});

const selected = ref<string | undefined>(firstOptionValue.value);

function onSelectionChanged(newVal: string) {
  const defaultVal = firstOptionValue.value;
  if (newVal === defaultVal) {
    selected.value = defaultVal;
    emit('filter-changed', undefined);
  } else {
    selected.value = newVal;
    emit('filter-changed', [newVal]);
  }
}

function onTooltipClicked() {
  emit('tooltip-clicked');
}

watch(
  () => value,
  (newVal) => {
    const defaultVal = firstOptionValue.value;
    if (!newVal.length || newVal[0] === defaultVal) {
      selected.value = defaultVal;
    } else {
      selected.value = newVal[0];
    }
  },
  { immediate: true },
);

watch(
  () => search,
  (newSearch) => {
    searchQuery.value = newSearch;
  },
  { immediate: true },
);
</script>
<style scoped lang="pcss">
.bitts-simple-radio-filter {
  @apply px-16 py-8;
}

:deep(.bitts-radio-group-cards .ant-radio-wrapper) {
  @apply border-0 border-b-0 px-8 py-0 rounded-8;
}

:deep(.c-bitts-empty-state-large) {
  @apply p-8 pb-0;
}

:deep(.bitts-simple-radio-filter__empty-state-icon) {
  @apply text-neutral-text-weak p-20 rounded-[36px];
  background: radial-gradient(
      136.96% 69.92% at 50.09% -12.42%,
      theme(colors.neutral.accent / 0.2) 0%,
      theme(colors.neutral.accent / 0) 100%
    ),
    linear-gradient(0deg, theme(colors.white), theme(colors.white));
}

:deep(.c-bitts-input .c-bitts-input__input .ant-input-clear-icon) {
  @apply hidden;
}
</style>
<style lang="pcss">
.bitts-tooltip.bitts-radio-group-tooltip .ant-tooltip-inner {
  @apply ml-8;
}
</style>
